import { useState, useMemo } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useTabNavigationStore } from '../../store/tabNavigationStore'
import { useAuthStore } from '../../store/authStore'
import { Card } from '../../components/ui/Card'
import { Button } from '../../components/ui/Button'
import { PageHeader } from '../../components/ui/PageHeader'
import { BottomSheet } from '../../components/ui/BottomSheet'
import { paymentSimpleApi } from '../../api/paymentSimple'
import { athletesApi } from '../../api/athletes'
import { kopecksToRubles, formatAmount } from '../../types/payment_simple'
import type { Athlete } from '../../types/api'

export function PaymentOverridesScreen() {
  const { getCurrentScreen, popScreen } = useTabNavigationStore()
  const currentAssistantId = useAuthStore((s) => s.currentAssistantId)
  const currentScreen = getCurrentScreen()
  const groupId = currentScreen.params?.groupId as string
  const groupName = (currentScreen.params?.groupName as string) || 'Группа'
  const queryClient = useQueryClient()

  const [showAddSheet, setShowAddSheet] = useState(false)
  const [editingAthleteId, setEditingAthleteId] = useState<string | null>(null)
  const [customPrice, setCustomPrice] = useState('')
  const [reason, setReason] = useState('')

  // Загружаем overrides для группы
  const { data: overrides, isLoading: overridesLoading } = useQuery({
    queryKey: ['group-overrides', groupId],
    queryFn: () => paymentSimpleApi.getGroupOverrides(groupId),
    enabled: !!groupId,
  })

  // Загружаем всех спортсменов ассистента, чтобы получить имена
  const { data: allAthletes } = useQuery({
    queryKey: ['athletes', currentAssistantId],
    queryFn: () => athletesApi.getByAssistantId(currentAssistantId!),
    enabled: !!currentAssistantId,
  })

  // Загружаем конфиг группы для базовой цены
  const { data: paymentConfig } = useQuery({
    queryKey: ['group-payment-config', groupId],
    queryFn: () => paymentSimpleApi.getGroupConfig(groupId),
    enabled: !!groupId,
    retry: false,
  })

  // Спортсмены этой группы
  const groupAthletes = useMemo(() => {
    if (!allAthletes || !groupId) return []
    return allAthletes.filter((a: Athlete) => a.group_id === groupId)
  }, [allAthletes, groupId])

  // Карта имён по ID
  const athleteNameMap = useMemo(() => {
    const map = new Map<string, string>()
    groupAthletes.forEach((a: Athlete) => map.set(a.id, a.name))
    return map
  }, [groupAthletes])

  // IDs спортсменов, у которых уже есть override
  const overrideAthleteIds = useMemo(() => {
    return new Set((overrides || []).map((o) => o.athlete_id))
  }, [overrides])

  // Спортсмены без override (для кнопки "Добавить")
  const athletesWithoutOverride = useMemo(() => {
    return groupAthletes.filter((a: Athlete) => !overrideAthleteIds.has(a.id))
  }, [groupAthletes, overrideAthleteIds])

  // Mutations
  const createMutation = useMutation({
    mutationFn: ({ athleteId, price, reason }: { athleteId: string; price: number; reason: string }) =>
      paymentSimpleApi.createOverride(athleteId, {
        group_id: groupId,
        custom_price: price,
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-overrides', groupId] })
      closeSheet()
    },
  })

  const updateMutation = useMutation({
    mutationFn: ({ athleteId, price, reason }: { athleteId: string; price: number; reason: string }) =>
      paymentSimpleApi.updateOverride(athleteId, {
        custom_price: price,
        reason,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-overrides', groupId] })
      closeSheet()
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (athleteId: string) => paymentSimpleApi.deleteOverride(athleteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['group-overrides', groupId] })
    },
  })

  const closeSheet = () => {
    setShowAddSheet(false)
    setEditingAthleteId(null)
    setCustomPrice('')
    setReason('')
  }

  const handleAddOverride = (athleteId: string) => {
    setEditingAthleteId(athleteId)
    setCustomPrice('')
    setReason('')
    setShowAddSheet(true)
  }

  const handleEditOverride = (athleteId: string) => {
    const override = overrides?.find((o) => o.athlete_id === athleteId)
    if (override) {
      setEditingAthleteId(athleteId)
      setCustomPrice(kopecksToRubles(override.custom_price).toString())
      setReason(override.reason)
      setShowAddSheet(true)
    }
  }

  const handleDeleteOverride = (athleteId: string) => {
    const name = athleteNameMap.get(athleteId) || 'Спортсмен'
    if (confirm(`Удалить индивидуальную цену для ${name}?\nБудет применяться базовая цена группы.`)) {
      deleteMutation.mutate(athleteId)
    }
  }

  const handleSave = () => {
    if (!editingAthleteId) return
    const price = parseFloat(customPrice)
    if (isNaN(price) || price < 0) {
      alert('Введите корректную цену (0 = полная льгота)')
      return
    }
    const isExisting = overrideAthleteIds.has(editingAthleteId)
    if (isExisting) {
      updateMutation.mutate({ athleteId: editingAthleteId, price, reason: reason.trim() })
    } else {
      createMutation.mutate({ athleteId: editingAthleteId, price, reason: reason.trim() })
    }
  }

  const inputStyle: React.CSSProperties = {
    width: '100%',
    padding: '10px 12px',
    borderRadius: '10px',
    border: '1px solid var(--separator)',
    backgroundColor: 'var(--bg-primary)',
    color: 'var(--text-primary)',
    fontSize: '17px',
    outline: 'none',
  }

  const labelStyle: React.CSSProperties = {
    display: 'block',
    fontSize: '13px',
    color: 'var(--text-hint)',
    marginBottom: '4px',
  }

  if (overridesLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <p style={{ color: 'var(--text-hint)' }}>Загрузка...</p>
      </div>
    )
  }

  return (
    <div className="p-4 space-y-4 pb-24">
      <PageHeader title="Индивидуальные цены" onBackClick={popScreen} />

      <p className="text-sm" style={{ color: 'var(--text-hint)' }}>
        {groupName}
        {paymentConfig && (
          <> &middot; Базовая цена: {kopecksToRubles(paymentConfig.base_price).toLocaleString('ru-RU')} ₽</>
        )}
      </p>

      {/* Список существующих overrides */}
      {overrides && overrides.length > 0 ? (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Спортсмены с инд. ценой ({overrides.length})
          </h3>
          <div className="space-y-1">
            {overrides.map((override) => (
              <div
                key={override.id}
                className="flex items-center justify-between py-3 px-2"
                style={{ borderBottom: '1px solid var(--separator)' }}
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[15px] font-medium truncate" style={{ color: 'var(--text-primary)' }}>
                    {athleteNameMap.get(override.athlete_id) || 'Спортсмен'}
                  </p>
                  <p className="text-xs truncate" style={{ color: 'var(--text-hint)' }}>
                    {override.reason}
                  </p>
                </div>
                <div className="flex items-center gap-2 ml-2">
                  <span
                    className="text-[15px] font-semibold whitespace-nowrap"
                    style={{ color: override.custom_price === 0 ? 'var(--success, #34C759)' : 'var(--accent)' }}
                  >
                    {override.custom_price === 0 ? 'Бесплатно' : formatAmount(override.custom_price)}
                  </span>
                  <button
                    onClick={() => handleEditOverride(override.athlete_id)}
                    className="text-xs px-2 py-1 rounded"
                    style={{ color: 'var(--accent)', border: '1px solid var(--accent)' }}
                  >
                    Изм.
                  </button>
                  <button
                    onClick={() => handleDeleteOverride(override.athlete_id)}
                    className="text-xs px-2 py-1 rounded"
                    style={{ color: 'var(--danger)', border: '1px solid var(--danger)' }}
                    disabled={deleteMutation.isPending}
                  >
                    Уд.
                  </button>
                </div>
              </div>
            ))}
          </div>
        </Card>
      ) : (
        <Card>
          <p className="text-center py-6" style={{ color: 'var(--text-hint)' }}>
            Нет индивидуальных цен. Все спортсмены платят базовую цену.
          </p>
        </Card>
      )}

      {/* Кнопка добавления */}
      {athletesWithoutOverride.length > 0 && (
        <Card>
          <h3 className="text-[17px] font-semibold mb-3" style={{ color: 'var(--text-primary)' }}>
            Назначить инд. цену
          </h3>
          <div className="space-y-1 max-h-60 overflow-y-auto">
            {athletesWithoutOverride.map((athlete: Athlete) => (
              <div
                key={athlete.id}
                onClick={() => handleAddOverride(athlete.id)}
                className="flex items-center justify-between py-2 px-2 rounded-lg cursor-pointer active:opacity-60"
                style={{ backgroundColor: 'var(--bg-secondary)' }}
              >
                <span className="text-[15px]" style={{ color: 'var(--text-primary)' }}>
                  {athlete.name}
                </span>
                <span className="text-xs" style={{ color: 'var(--accent)' }}>
                  + Назначить
                </span>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Bottom Sheet для создания/редактирования override */}
      <BottomSheet
        isOpen={showAddSheet}
        onClose={closeSheet}
        title={overrideAthleteIds.has(editingAthleteId || '') ? 'Изменить цену' : 'Индивидуальная цена'}
      >
        <div className="space-y-4 px-4 pb-4">
          {editingAthleteId && (
            <div>
              <p className="text-sm mb-1" style={{ color: 'var(--text-hint)' }}>Спортсмен:</p>
              <p className="font-medium" style={{ color: 'var(--text-primary)' }}>
                {athleteNameMap.get(editingAthleteId) || 'Спортсмен'}
              </p>
            </div>
          )}

          <div>
            <label style={labelStyle}>Цена (₽/месяц) *</label>
            <input
              type="number"
              value={customPrice}
              onChange={(e) => setCustomPrice(e.target.value)}
              placeholder={paymentConfig ? kopecksToRubles(paymentConfig.base_price).toString() : '0'}
              style={inputStyle}
              min="0"
              step="100"
              autoFocus
            />
            <p className="text-xs mt-1" style={{ color: 'var(--text-hint)' }}>
              0 = полная льгота (бесплатно)
              {paymentConfig && (
                <> &middot; Базовая: {kopecksToRubles(paymentConfig.base_price).toLocaleString('ru-RU')} ₽</>
              )}
            </p>
          </div>

          <div>
            <label style={labelStyle}>Комментарий</label>
            <textarea
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Например: льгота, скидка за 2 ребёнка, многодетная семья"
              style={{ ...inputStyle, minHeight: '80px', resize: 'vertical' }}
            />
          </div>

          <div className="flex gap-2 pt-2">
            <Button
              onClick={handleSave}
              disabled={createMutation.isPending || updateMutation.isPending}
              fullWidth
            >
              {createMutation.isPending || updateMutation.isPending ? 'Сохранение...' : 'Сохранить'}
            </Button>
            <Button variant="secondary" onClick={closeSheet} fullWidth>
              Отмена
            </Button>
          </div>

          {(createMutation.isError || updateMutation.isError) && (
            <p className="text-sm text-center" style={{ color: 'var(--danger)' }}>
              {((createMutation.error || updateMutation.error) as Error)?.message || 'Ошибка сохранения'}
            </p>
          )}
        </div>
      </BottomSheet>
    </div>
  )
}
